import type React from 'react';

export enum AuthType {
  OAUTH = 'oauth',
  API_KEY = 'api_key',
  OAUTH_WITH_CREDENTIALS = 'oauth_with_credentials',
  // Multi-field credentials validated via a side-effect-free lookup call
  // (which returns provider data to populate a second step) before the
  // real connect call — no OAuth leg. See SpotDraftConnectDrawer.
  CREDENTIALS_WITH_LOOKUP = 'credentials_with_lookup',
}

export interface OAuthConfig {
  formMethod: string;
  formUrl: string;
}

export interface ApiKeyConfig {
  endpoint: string;
  fieldLabel: string;
  fieldPlaceholder: string;
}

export interface CredentialField {
  name: string;
  label: string;
  placeholder: string;
  validationSchema: any;
}

export interface CredentialsConfig {
  endpoint: string;
  fields: CredentialField[];
  oauthConfig: OAuthConfig;
}

export interface ProviderConfig {
  id: string;
  label: string;
  img?: string;
  description: string;
  descriptionForm?: string;
  note?: string;
  icon?: React.ComponentType;
  iconProps?: Record<string, any>;
  authType: AuthType;
  integrationType: string;
  oauthConfig?: OAuthConfig;
  apiKeyConfig?: ApiKeyConfig;
  credentialsConfig?: CredentialsConfig;
}

export interface ESignatureConnectionProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  providerId: string;
  onTriggerOAuth?: () => void;
}

export interface ConnectApiKeyPayload {
  integration_type: string;
  api_key: string;
}

export interface ConnectCredentialsPayload {
  integration_type: string;
  client_id: string;
  client_secret: string;
}

export interface ConnectApiKeyRequest {
  account_integration: ConnectApiKeyPayload;
}

export interface ConnectCredentialsRequest {
  account_integration: ConnectCredentialsPayload;
}

export interface ApiResponse {
  success?: boolean;
  message?: string;
  error?: string;
}

export interface FormField {
  name: string;
  label: string;
  placeholder: string;
  required: boolean;
  validationSchema: any;
  type: string;
  customOnChange: (e: any) => void;
}

export type ESignatureProvider =
  | 'docusign'
  | 'pandadocs'
  | 'adobe'
  | 'spotdraft';

export interface IntegrationData {
  id?: string;
  integration_type: string;
  [key: string]: any;
}

export interface SelectedIntegration {
  mode?: string;
  data: IntegrationData;
  cardConfig: Partial<ProviderConfig>;
}

export interface CardConfig {
  id: string;
  label: string;
  subLabel: string;
  img?: string;
  icon?: React.ComponentType;
  iconProps?: Record<string, any>;
  isConnected: boolean;
  isLoading: boolean;
  // Omitted for providers with no edit flow (e.g. SpotDraft, whose edit
  // endpoint 404s) — SettingsToggleCard hides the pencil when unset.
  onEdit?: () => void;
  onToggle: () => void;
}

export interface EsignEditProps {
  modalActions: {
    isOpen: boolean;
    onClose: () => void;
    onOpen: () => void;
  };
  isAdmin?: boolean;
  offerId?: string;
  onSuccess?: () => void;
  integrationType?: string;
  jobId?: string;
  // When true (DocuSign only), the drawer's single CTA becomes "Preview on Docusign" — it creates a
  // draft envelope and redirects the current tab to the embedded sender view (same-tab, to dodge
  // popup blockers) instead of sending immediately.
  enablePreview?: boolean;
}

export interface FormValues {
  subject?: string;
  body?: string;
  company_signer_required?: string;
  signing_order?: string;
  company_signer_id?: string;
  // DocuSign per-recipient toggle + per-recipient emails + envelope CC (Kula user IDs).
  custom_message_per_recipient?: boolean;
  company_email_subject?: string;
  company_email_body?: string;
  cc_email_subject?: string;
  cc_email_body?: string;
  cc_user_ids?: number[];
}

export interface EsignCompanySigner {
  id: number | string;
  name?: string;
  avatar?: string;
}

// Account default template returned by GET /esign_envelopes/default (response.data is untyped).
export interface DefaultEsignEnvelope {
  signing_order?: 'company_signer_first' | 'candidate_first' | string;
  email_subject?: string;
  email_body?: string;
  custom_message_per_recipient?: boolean;
  company_email_subject?: string | null;
  company_email_body?: string | null;
  cc_email_subject?: string | null;
  cc_email_body?: string | null;
  company_signer_required?: boolean;
  company_signer?: EsignCompanySigner;
  cc_user_ids?: number[];
}

export interface UpdateEsignPayload {
  company_signer_required: boolean;
  signing_order: 'company_signer_first' | 'candidate_first';
  email_subject: string;
  email_body: string;
  company_signer_id?: string;
  // DocuSign per-recipient toggle + per-recipient emails + envelope CC (Kula user IDs).
  custom_message_per_recipient?: boolean;
  company_email_subject?: string | null;
  company_email_body?: string | null;
  cc_email_subject?: string | null;
  cc_email_body?: string | null;
  cc_user_ids?: number[];
}

export interface SendEsignPayload {
  esign_envelope: {
    email_subject: string;
    email_body: string;
    company_signer_id?: string;
    // DocuSign per-recipient toggle + per-recipient emails + envelope CC (Kula user IDs).
    custom_message_per_recipient?: boolean;
    company_email_subject?: string;
    company_email_body?: string;
    cc_email_subject?: string;
    cc_email_body?: string;
    cc_user_ids?: number[];
  };
}

export type SpotDraftRegion = 'us' | 'in' | 'eu' | 'me';

export interface SpotDraftContractType {
  id: number;
  name: string;
}

export interface SpotDraftContractTypesPayload {
  client_id: string;
  client_secret: string;
  region: SpotDraftRegion;
}

export interface SpotDraftContractTypesResponse {
  contract_types: SpotDraftContractType[];
}

export interface SpotDraftConnectPayload {
  integration_type: string;
  api_key: string;
  spotdraft_client_id: string;
  spotdraft_region: SpotDraftRegion;
  spotdraft_contract_type_id: number;
}

/* -------------------------------------------------------------------------- *
 * DocuSign stage activity (flag: t_docusign_on_all_stages)
 * -------------------------------------------------------------------------- */

/** Per-document outcome from POST …/validate_esign_documents. */
export type EsignDocumentStatus = 'verified' | 'error';

export interface EsignDocumentValidation {
  filename: string;
  status: EsignDocumentStatus;
  valid_tokens?: string[];
  invalid_tokens?: string[];
  /** `err_*` code, present only when status is 'error'. */
  error?: string;
}

export interface EsignValidateDocumentsResponse {
  documents: EsignDocumentValidation[];
}

/** A document already persisted on the stage action. */
export interface EsignActivityDocument {
  id: number | string;
  filename: string;
  url?: string;
  content_type?: string;
  byte_size?: number;
  /**
   * Anchors the backend found in this document, bare
   * (`docusign.candidate_signature`).
   *
   * Present on a saved activity's documents, which is what lets the wizard show
   * the token list for documents it never uploaded in this session.
   */
  tokens?: string[];
}

/**
 * One row in the wizard's document list.
 *
 * `file` is set for a newly picked upload, `id` for one already persisted on the
 * activity. Exactly one of the two is present: the save payload sends the File
 * for new rows and the bare id for retained rows, which is how the backend
 * distinguishes "keep this" from "replace with this".
 */
export interface EsignWizardDocument {
  /** Stable list key — not the server id, which new rows do not have yet. */
  key: string;
  filename: string;
  file?: File;
  id?: number | string;
  url?: string;
  validation?: EsignDocumentValidation;
}

/** The esign branch of a stage-action payload (GET …/ats_job_stage_actions/:id). */
export interface EsignActivityConfig {
  id?: number | string;
  kind?: string;
  label?: string;
  position?: number;
  esign_documents?: EsignActivityDocument[];
  employer_signer_required?: boolean;
  company_signer?: EsignCompanySigner;
  company_signer_id?: number | string;
  signing_order?: 'company_signer_first' | 'candidate_first';
  custom_message_per_recipient?: boolean;
  email_subject?: string;
  email_body?: string;
  company_email_subject?: string | null;
  company_email_body?: string | null;
  cc_email_subject?: string | null;
  cc_email_body?: string | null;
  cc_user_ids?: number[];
}

/**
 * What the wizard hands the form-data builder. Mirrors UpdateEsignPayload plus
 * the documents, and keeps the offer flow's `company_signer_required` name — the
 * rename to `employer_signer_required` happens once, in getEsignFormData.
 */
export interface EsignActivityPayload {
  label?: string;
  position?: number;
  documents: EsignWizardDocument[];
  company_signer_required: boolean;
  /**
   * 'company_signer_first' | 'candidate_first' in practice, typed as a string
   * because that is what the shared composer's getModel returns — narrowing it
   * here would only force a cast at the one call site that produces it.
   */
  signing_order: string;
  email_subject: string;
  email_body: string;
  company_signer_id?: string | number;
  custom_message_per_recipient?: boolean;
  company_email_subject?: string;
  company_email_body?: string;
  cc_email_subject?: string;
  cc_email_body?: string;
  cc_user_ids?: number[];
  /** Set when the activity is cloned; the backend copies this activity's documents. */
  source_ats_job_stage_action_id?: number | string;
}

/**
 * One entry from GET /esign_documents/supported_tokens.
 *
 * ~72 of them: six DocuSign placement anchors plus the merge tokens, across the
 * groups Signature, Candidate, Job, Sender and Organization. The anchors place
 * signing boxes and substitute nothing; the merge tokens do the opposite.
 */
export interface EsignSupportedToken {
  token: string;
  label: string;
  group: string;
  /**
   * Set-level, not per document: one document in the set must carry it, not
   * every one. Only the candidate signature anchor has it today.
   */
  required?: boolean;
}

/** POST /esign_documents/{send,preview,…} → 202. Also the poll response shape. */
export interface EsignDocumentEnvelope {
  id: number | string;
  status: string | null;
  external_id_present: boolean;
  error: { code?: string; message?: string } | null;
}
